import { useCallback, useEffect, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { colors } from '@/assets/styles/colors';
import { Text } from '@/components/ui/text/Text';
import { useTranslation } from '@/i18n';
import { ROUTE_DEFINITIONS } from '@/routes';
import { useUI } from '@/hooks/ui/useUI';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { fractionalLeaveService } from '@/api/domains/fractionalLeave/fractionalLeave.service';
import {
  FRACTIONAL_LEAVE_STATUS_FILTER,
  FRACTIONAL_LEAVE_DEFAULT_PAGE_SIZE,
  FRACTIONAL_LEAVE_LIST_REFRESH_EVENT,
} from '@/api/domains/fractionalLeave/fractionalLeave.types';
import { useFractionalLeaveExport } from './useFractionalLeaveExport';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import type {
  FractionalLeave,
  FractionalLeaveStatusFilter,
} from '@/api/domains/fractionalLeave/fractionalLeave.types';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';

// ── Local UI constants ─────────────────────────────────────────────────────

const FRACTIONAL_LEAVE_ACTION_IDS = {
  EDIT:    'fractionalLeave-edit',
  DISABLE: 'fractionalLeave-disable',
  ENABLE:  'fractionalLeave-enable',
} as const;

const FRACTIONAL_LEAVE_FORM_PANEL_KEY = 'FractionalLeaveFormPanel' as const;
const FRACTIONAL_LEAVE_CREATE_PANEL_KEY = 'FractionalLeaveCreatePanel' as const;


// ── Type exports for useFractionalLeaveExport ──────────────────────────────

export type StatusFilter = FractionalLeaveStatusFilter;
export type SortKey = string | undefined;
export type SortDirection = 'ASC' | 'DESC';


// ── Return type interface ──────────────────────────────────────────────────

export interface UseFractionalLeaveListResult {
  rows: FractionalLeave[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<FractionalLeave>[];
  actions: TableAction<FractionalLeave>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  statusFilter: FractionalLeaveStatusFilter;
  onStatusFilterChange: (value: FractionalLeaveStatusFilter) => void;
  statusFilterOptions: { value: string; label: string }[];
  isLoading: boolean;
  isExporting: boolean;
  canCreate: boolean;
  openCreatePanel: () => void;
  onExport: (format: import('@/types/fileExport.types').UIExportFormat) => Promise<void>;
}

// ── Server sort key mapping (frontend column key → backend field) ──────────

const ORDER_BY_MAP: Record<string, string> = {
  id: 'id',
  name: 'name',
  description: 'description',
  hours: 'hours',
  deductible: 'deductible',
  status: 'isActive',
};

// ── Hook ───────────────────────────────────────────────────────────────────

type FractionalLeaveFilters = { statusFilter: FractionalLeaveStatusFilter };

export function useFractionalLeaveList(): UseFractionalLeaveListResult {
  const { t: translate } = useTranslation();
  const { showNotification, showApiError, openSidePanel } = useUI();
  const { isLoaded, canView, canCreate, canEdit } = usePagePermissions();
  const navigate = useNavigate();

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<FractionalLeaveFilters>,
      _signal: AbortSignal,
    ): Promise<PaginatedFetchResult<FractionalLeave> | null> => {
      const response = await fractionalLeaveService.list({
        page: params.page,
        pageSize: params.pageSize,
        searchText: params.searchText,
        statusFilter: params.filters.statusFilter,
        sort: params.sort
          ? { key: params.sort.key, direction: params.sort.direction.toLowerCase() as 'asc' | 'desc' }
          : undefined,
      });
      if (!response.success || !response.data) {
        showApiError(response);
        return null;
      }
      return { items: response.data.data, totalRecords: response.data.totalRecords };
    },
    [showApiError],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading: isLoading,
    onSearch,
    onSortChange,
    sort,
    filters,
    setFilters,
    debouncedSearchText,
    refetch,
  } = usePaginatedList<FractionalLeave, FractionalLeaveFilters>({
    fetchFn,
    initialFilters: { statusFilter: FRACTIONAL_LEAVE_STATUS_FILTER.ALL },
    initialPageSize: FRACTIONAL_LEAVE_DEFAULT_PAGE_SIZE,
    orderByMap: ORDER_BY_MAP,
    deduplicate: true,
    enabled: isLoaded && canView,
    screenCode: ROUTE_DEFINITIONS.LISTA_PERMISOS_FRACCION.code,
  });

  // ── Export hook ────────────────────────────────────────────────────────
  const { isExporting, onExport } = useFractionalLeaveExport({
    searchText: debouncedSearchText || undefined,
    statusFilter: filters.statusFilter,
    sortKey: sort?.key,
    sortDirection: sort?.direction ?? 'ASC',
  });

  // ── Permission guard: redirect if no read access ────────────────────────
  useEffect(() => {
    if (isLoaded && !canView) {
      void navigate(ROUTE_DEFINITIONS.DASHBOARD_ASISTENCIA.path);
    }
  }, [isLoaded, canView, navigate]);

  // ── List refresh event (dispatched by FractionalLeaveFormPanel after save) ─
  useEffect(() => {
    const handleRefresh = () => { refetch(); };
    window.addEventListener(FRACTIONAL_LEAVE_LIST_REFRESH_EVENT, handleRefresh);
    return () => {
      window.removeEventListener(FRACTIONAL_LEAVE_LIST_REFRESH_EVENT, handleRefresh);
    };
  }, [refetch]);

  // ── Toggle active status (with optimistic update + rollback) ───────────
  const executeToggleStatus = useCallback(async (row: FractionalLeave, newStatus: boolean) => {
    const response = await fractionalLeaveService.toggleStatus(row.id, newStatus);

    if (response.success) {
      showNotification('success', translate('fractionalLeave:messages.toggleStatusSuccess'));
      refetch();
    } else {
      showApiError(response);
    }
  }, [refetch, showNotification, showApiError, translate]);

  // ── Open create side panel ─────────────────────────────────────────────
  const openCreatePanel = useCallback(() => {
    openSidePanel({
      component: FRACTIONAL_LEAVE_CREATE_PANEL_KEY,
      title: translate('fractionalLeave:form.createTitle'),
      props: {},
    });
  }, [openSidePanel, translate]);

  // ── Open edit side panel ───────────────────────────────────────────────
  const openEditPanel = useCallback((row: FractionalLeave) => {
    openSidePanel({
      component: FRACTIONAL_LEAVE_FORM_PANEL_KEY,
      title: translate('fractionalLeave:form.editTitle'),
      props: { record: row },
    });
  }, [openSidePanel, translate]);

  // ── Columns ────────────────────────────────────────────────────────────
  const columns: TableColumn<FractionalLeave>[] = useMemo(() => [
    {
      key: 'id',
      header: translate('fractionalLeave:columns.id'),
      sortable: true,
    },
    {
      key: 'name',
      header: translate('fractionalLeave:columns.name'),
      sortable: true,
    },
    {
      key: 'description',
      header: translate('fractionalLeave:columns.description'),
      sortable: true,
    },
    {
      key: 'hours',
      header: translate('fractionalLeave:columns.hours'),
      sortable: true,
    },
    {
      key: 'deductible',
      header: translate('fractionalLeave:columns.deductible'),
      sortable: true,
      render: (row: FractionalLeave) =>
        row.deductible
          ? translate('fractionalLeave:booleans.yes')
          : translate('fractionalLeave:booleans.no'),
    },
    {
      key: 'status',
      header: translate('fractionalLeave:columns.status'),
      sortable: true,
      render: (row: FractionalLeave) => (
        <Text
          variant="subheader-medium"
          color={row.isActive ? colors.feedback.success400 : colors.grays.neutral99}
          as="span"
        >
          {row.isActive
            ? translate('fractionalLeave:statuses.active')
            : translate('fractionalLeave:statuses.inactive')}
        </Text>
      ),
    },
  ], [translate]);

  // ── Actions ────────────────────────────────────────────────────────────
  const actions: TableAction<FractionalLeave>[] = useMemo(() => [
    {
      id: FRACTIONAL_LEAVE_ACTION_IDS.EDIT,
      label: translate('common:tableActions.edit'),
      icon: 'edit',
      hidden: () => !canEdit,
      onClick: openEditPanel,
    },
    {
      id: FRACTIONAL_LEAVE_ACTION_IDS.DISABLE,
      label: translate('common:tableActions.disable'),
      icon: 'close',
      hidden: (row) => !canEdit || !row.isActive,
      onClick: (row) => void executeToggleStatus(row, false),
      variant: 'danger',
    },
    {
      id: FRACTIONAL_LEAVE_ACTION_IDS.ENABLE,
      label: translate('common:tableActions.enable'),
      icon: 'checkCircle',
      hidden: (row) => !canEdit || row.isActive,
      onClick: (row) => void executeToggleStatus(row, true),
    },
  ], [translate, canEdit, openEditPanel, executeToggleStatus]);

  // ── Status filter options ──────────────────────────────────────────────
  const statusFilterOptions = useMemo(() => [
    { value: FRACTIONAL_LEAVE_STATUS_FILTER.ALL,      label: translate('fractionalLeave:filters.all') },
    { value: FRACTIONAL_LEAVE_STATUS_FILTER.ACTIVE,   label: translate('fractionalLeave:filters.active') },
    { value: FRACTIONAL_LEAVE_STATUS_FILTER.INACTIVE, label: translate('fractionalLeave:filters.inactive') },
  ], [translate]);

  // ── Handlers ───────────────────────────────────────────────────────────
  const onStatusFilterChange = useCallback((value: FractionalLeaveStatusFilter) => {
    setFilters({ statusFilter: value });
  }, [setFilters]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    onSearch,
    onSortChange,
    statusFilter: filters.statusFilter,
    onStatusFilterChange,
    statusFilterOptions,
    isLoading,
    isExporting,
    canCreate,
    openCreatePanel,
    onExport,
  };
}
